import { PlateformeDto } from 'api/plateforme';
import { ReferentialKey } from 'api/referential';
import { PlateformesPickerForm } from 'back-office/shared/form/PlateformesPickerForm';
import { Button } from 'components/ui/Button';
import { SectionOutlet } from 'components/layout/Section/SectionOutlet';
import { Sizes } from 'enums/sizes';
import { useReferentialUtils } from 'features/referentials/hooks/useReferentialUtils';
import { useDialog } from 'hooks/useDialog';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { NsKey } from 'enums/NsKey';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI } from 'services/swagger-api';
import PlateformeCard from '../plateformes-view/PlateformeCard';
import { useProductViewContext } from './ProductViewContext';

const ProductPlateformes: React.FC<{ productId: string }> = ({ productId }) => {
  const [plateformes, setPlateformes] = useState<PlateformeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { handleAddPlateformeKeys } = useProductViewContext();

  const { openDialog } = useDialog();
  const { t } = useTranslation(NsKey.Common);
  // The hook member is misnamed: it resolves an ID FROM a code, not the reverse.
  const { getReferentialCodeById: getReferentialIdByCode } = useReferentialUtils();

  const fetchPlateformes = useCallback(async () => {
    try {
      const response = await productsAPI.getPlateformes({ productId });
      setPlateformes(response.plateformes);
      handleAddPlateformeKeys(response.plateformes.map(org => org.code));
    } catch (error) {
      console.error('Error fetching plateformes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [productId, handleAddPlateformeKeys]);

  const linkPlateformesToProduct = useCallback(
    async (selectedCodes: string[]) => {
      /* Lossless code -> id translation: an unknown code used to be silently
         dropped, which unlinked a plateforme the user had kept selected. */
      const plateformeIds: string[] = [];
      const unknownCodes: string[] = [];
      selectedCodes.forEach(code => {
        const id = getReferentialIdByCode(ReferentialKey.Plateforme, code);
        if (id) {
          plateformeIds.push(id);
        } else {
          unknownCodes.push(code);
        }
      });

      if (unknownCodes.length > 0) {
        const message = `Plateforme inconnue du référentiel : ${unknownCodes.join(', ')}`;
        toast.error(message);
        // Thrown so the picker stays open with the selection intact.
        throw new Error(message);
      }

      await productsAPI.linkPlateformes({
        productId,
        genericLinkPlateformesCommand: { plateformeIds }
      });
    },
    [getReferentialIdByCode, productId]
  );

  const openLinkPlateformesForm = useCallback(() => {
    openDialog(
      {
        title: t('PLATEFORME_PICKER.MANAGE_PRODUCT'),
        size: Sizes['3xl']
      },
      <PlateformesPickerForm
        initialSelectedCodes={plateformes.map(org => org.code)}
        onSubmit={linkPlateformesToProduct}
        onSuccess={fetchPlateformes}
      />
    );
  }, [openDialog, t, linkPlateformesToProduct, plateformes, fetchPlateformes]);

  useEffect(() => {
    fetchPlateformes();
  }, [fetchPlateformes]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, index) => (
          <div
            key={index}
            className="h-[200px] rounded-xl bg-gray-200 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (plateformes.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-12 text-gray-500">
        <svg
          className="w-16 h-16 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <p className="text-lg font-medium mb-4">Aucune plateforme trouvée</p>
        <Button color="primary" onClick={openLinkPlateformesForm}>
          Créer une plateforme
        </Button>
      </div>
    );
  }

  return (
    <SectionOutlet title="Plateformes associées">
      <div className="grid md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        {plateformes.map(plateforme => (
          <PlateformeCard
            key={plateforme.id}
            plateforme={plateforme}
            onClick={() =>
              navigate(`/back-office/plateformes/${plateforme.id}`)
            }
            onDelete={() => {}}
          />
        ))}

        {/* Bouton d'ajout */}
        <div
          onClick={openLinkPlateformesForm}
          className="relative rounded-xl cursor-pointer transition-all duration-200 hover:-translate-y-1 bg-white border-2 border-dashed border-gray-300 hover:border-primary hover:shadow-lg"
        >
          <div className="p-6 h-full flex flex-col items-center justify-center gap-3">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">
              Ajouter une plateforme
            </span>
          </div>
        </div>
      </div>
    </SectionOutlet>
  );
};

export default ProductPlateformes;
